/**
 * HR types and query hooks.
 *
 * Two shapes for one row, mirroring the backend: `DirectoryEntry` is what any
 * signed-in user gets, `Employee` is the personnel record behind `hr.view`.
 * Keeping them apart in TypeScript too means a component that renders the
 * directory cannot accidentally read a field it was never sent.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { businessYmd } from './businessDate'
import { nameFromParts } from './names'
import type { LockedRun } from './rateHistory'

export const ORG_ROLES = [
  'Advisor',
  'Admin',
  'Manager',
  'Coordinator',
  'Team lead',
  'Member',
] as const
export type OrgRole = (typeof ORG_ROLES)[number]

interface ProductRole {
  product_service_id: string
  role: OrgRole
}

export interface ProductService {
  id: string
  title: string
  type_of: string
  shift_type: string
  is_active: boolean
}

export interface DirectoryEntry {
  id: string
  staff_id: number | null
  display_name: string
  first_name: string
  last_name: string
  first_name_mn: string
  last_name_mn: string
  staff_code: string
  email: string
  /** Mobile number. The directory serves it for Active people only (blank for leavers). */
  phone: string
  position: string
  position_mn: string
  position_zh: string
  profession: string
  team: string
  work_type: string
  office: string
  status: 'Active' | 'Inactive'
  supervisor_id: string | null
  trg_entity: string[]
  product_roles: ProductRole[]
}

/** The person's name, not their sign-in handle. `display_name` sometimes
 *  carries a login handle (e.g. `AmarjargalB`) rather than a real name --
 *  same first-name/last-name precedence as backend/app/services/payroll.py
 *  and the time person picker. */
export function personName(person: Pick<DirectoryEntry, 'first_name' | 'last_name' | 'display_name'>): string {
  return nameFromParts(person.first_name, person.last_name) || person.display_name
}

export interface Employee extends DirectoryEntry {
  /** No longer served (`id` is both record and login); kept optional so a stale cached response is not a type error. */
  user_id?: never
  peer_id: string | null
  /** The staff member's own personal mobile, so behind `hr.view`, not on `DirectoryEntry`. */
  phone: string
  personal_email: string
  dob: string | null
  gender: string
  country: string
  degree: string
  work_condition: string
  emergency_contact_name: string
  emergency_contact_phone: string
  has_special_needs: boolean
  /** A third party's (a child's) data, erased like `has_special_needs` in `services/pii_erasure.py`. */
  dependent_children?: number
  worked_since: string | null
  full_time_start: string | null
  months_before?: number
  end_of_contract?: string | null
  resignation_date: string | null
  al_type: string
  al_days: string
  psl_days: string
  cl_days: string
  spl_days: string
  taken_sl_days: string
  /** The civil registration number (2 Cyrillic letters + 8 digits). One of the five `hr.extra` fields. */
  registration_no?: string
  /** True when the server withheld the five `hr.extra` fields from this caller. An absent extra with this
   *  false is simply not recorded; with this true it is unknown to the caller and must not read as blank. */
  extras_hidden?: boolean
  can_sign_in?: boolean
  password_only?: boolean
  above_member?: boolean
  microsoft_linked?: boolean
  notes?: string | null
  /** Only present when the record contradicts itself (e.g. Active with a past
   *  resignation date and a contract still running) -- see `_record_warning`
   *  in services/hr.py. Absent, not null, when there is nothing to flag. */
  record_warning?: string
  /** False when the server would answer an unchanged start date as a re-hire (the employment spell is already closed). Absent unless the record is Inactive. */
  can_undo_deactivation?: boolean | null
  /** True only when this response withheld a populated personal-data field (an Inactive
   *  person read without `hr.edit`); a blank field alone is ambiguous. */
  personal_data_hidden: boolean
}

export interface EmployeeRate {
  id: string
  employee_id: string
  effective_since: string
  type_of: string
  rate: string
  /** Set only on a `Daily+Hourly` row, where `rate` is the daily rate. */
  hourly_rate?: string | null
  task_rate: string
  /** A rate row has a rule set OR a flat `task_rate`, never both. */
  task_rule_set_id?: string | null
  additional: string
  transit_add: boolean
  soc_ins_code: string
  account: string
  /** Derived by the database from the saved `account`; output only. */
  account_status?: string
}

/** The rate in force on the business day, on an HR list row. */
interface CurrentRate {
  effective_since: string
  type_of: string
  rate: string
  task_rate: string
  additional: string
  soc_ins_code: string
}

/** A row of the HR list. `salary` is ABSENT unless the list was asked
 *  `with_salary` by a `salary.view` / `hr.edit` holder; `current_rate: null`
 *  inside it is "allowed, and no rate in force" -- never a zero. */
export type BankAccountStatus = 'ok' | 'none' | 'no_primary'

export type HrListRow = DirectoryEntry &
  Partial<Omit<Employee, keyof DirectoryEntry>> & {
    salary?: {
      current_rate: CurrentRate | null
      /** Whether the bank file can pay this person; never the number. */
      bank_account_status?: BankAccountStatus | null
    }
  }

/** One entry of `GET /api/hr/employees/{id}/role-history`. Open to every signed-in
 *  user: only this slice of the snapshot is sent, so nothing here needs `hr.view`. `role: null` is a real state
 *  ("unassigned as of this date"), not a missing value. */
export interface RoleTransition {
  product_service_id: string
  role: string | null
  at: string
  baseline: boolean
}

export const hrKeys = {
  directory: (p: Record<string, unknown>) => ['hr', 'directory', p] as const,
  employees: (p: Record<string, unknown>) => ['hr', 'employees', p] as const,
  employee: (id: string) => ['hr', 'employee', id] as const,
  products: ['hr', 'products'] as const,
  rates: (id: string) => ['hr', 'rates', id] as const,
  history: (id: string, kind?: string) => ['hr', 'history', id, kind] as const,
  roleHistory: (id: string) => ['hr', 'role-history', id] as const,
  listColumns: ['hr', 'list-columns'] as const,
  attachableLogins: ['hr', 'attachable-logins'] as const,
}

interface ListParams {
  search?: string
  team?: string
  include_inactive?: boolean
  /** Rows per page (default 500, cap 1000). The cap applies BEFORE client-side search, and
   *  /hr filters locally because its org chart is drawn from the same rows, so it asks for the max. */
  limit?: number
  /** Also return each row's current rate (the server drops it for a
   *  caller without `salary.view` / `hr.edit`). */
  with_salary?: boolean
}

function clean(params: ListParams) {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
  )
}

/**
 * The directory list. `full` asks for the personnel projection instead, which
 * the caller may only do when the user holds `hr.view` -- the endpoint 403s
 * otherwise, and a 403 here would blank the page for everyone else.
 */
export function useDirectory(params: ListParams, full: boolean) {
  const query = clean(params)
  return useQuery({
    queryKey: full ? hrKeys.employees(query) : hrKeys.directory(query),
    queryFn: async () =>
      (
        await api.get<HrListRow[]>(full ? '/api/hr/employees' : '/api/hr/directory', {
          params: query,
        })
      ).data,
  })
}

/** The organization's two default column lists for the HR list -- one
 *  for viewers without records access (`staff`, directory columns only) and
 *  one for those with it (`hr`). `null` means nothing is saved for that
 *  audience, and the hard-coded `DEFAULT_COLUMN_IDS` apply. */
export type ColumnAudience = 'staff' | 'hr'
export interface HrListColumns {
  staff: string[] | null
  hr: string[] | null
}

/** Any signed-in reader. A failed or pending read is simply no org default --
 *  the list must render on the hard-coded columns, never wait on this. */
export function useHrListColumns() {
  return useQuery({
    queryKey: hrKeys.listColumns,
    queryFn: async () => (await api.get<HrListColumns>('/api/hr/list-columns')).data,
    retry: false,
    staleTime: 5 * 60_000,
  })
}

/** `hr.edit` only (the server refuses anyone else). Saves ONE audience's list;
 *  an empty list clears it. A `staff` list naming a non-directory column is a 422. */
export function useSaveHrListColumns() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { audience: ColumnAudience; columnIds: string[] }) =>
      (
        await api.put<HrListColumns>('/api/hr/list-columns', {
          audience: input.audience,
          column_ids: input.columnIds,
        })
      ).data,
    onSuccess: (saved) => queryClient.setQueryData(hrKeys.listColumns, saved),
  })
}

export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: hrKeys.employee(id ?? ''),
    queryFn: async () => (await api.get<Employee>(`/api/hr/employees/${id}`)).data,
    enabled: Boolean(id),
  })
}

/** One login `POST /api/hr/employees` accepts as `user_id`. */
export interface AttachableLogin {
  id: string
  username: string
  first_name: string
  last_name: string
  display_name: string
  tier: string
}

/** Needs `hr.edit`; a non-admin is offered member-tier logins only. `enabled` keeps the call to the new-record form. */
export function useAttachableLogins(enabled: boolean) {
  return useQuery({
    queryKey: hrKeys.attachableLogins,
    queryFn: async () => (await api.get<AttachableLogin[]>('/api/hr/attachable-logins')).data,
    enabled,
  })
}

/** `includeInactive` adds Config-retired products, for records that already hold one. */
export function useProducts(includeInactive = false) {
  return useQuery({
    queryKey: includeInactive ? [...hrKeys.products, 'all'] : hrKeys.products,
    queryFn: async () =>
      (
        await api.get<ProductService[]>('/api/hr/products', {
          params: includeInactive ? { include_inactive: true } : undefined,
        })
      ).data,
  })
}

export function useSaveEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: unknown }) => {
      const response = id
        ? await api.put<Employee>(`/api/hr/employees/${id}`, payload)
        : await api.post<Employee>('/api/hr/employees', payload)
      return response.data
    },
    onSuccess: (employee) => {
      queryClient.invalidateQueries({ queryKey: ['hr'] })
      queryClient.setQueryData(hrKeys.employee(employee.id), employee)
    },
  })
}

/** Manual erasure of one departed person, admin-triggered -- no bulk variant, on purpose. */
export function useErasePersonalData() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ fields_cleared: string[] }>(`/api/hr/employees/${id}/erase-pii`)).data,
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.employee(id) })
    },
  })
}

export function useSaveRoles() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      roles,
      trgEntity,
    }: {
      id: string
      roles: ProductRole[]
      trgEntity: string[]
    }) =>
      (
        // Returns the DIRECTORY shape, not the personnel record: the endpoint is gated on
        // `hr.edit`, not `hr.view`.
        await api.put<DirectoryEntry>(`/api/hr/employees/${id}/roles`, {
          roles,
          trg_entity: trgEntity,
        })
      ).data,
    onSuccess: () => {
      // Invalidate rather than seeding the cache with the response: the body is
      // narrower than `Employee` now, so writing it into the employee cache
      // would blank the personal fields for a reader who is entitled to them.
      queryClient.invalidateQueries({ queryKey: ['hr'] })
    },
  })
}

export function useRates(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: hrKeys.rates(id ?? ''),
    queryFn: async () =>
      (await api.get<EmployeeRate[]>(`/api/hr/employees/${id}/rates`)).data,
    enabled: Boolean(id) && enabled,
  })
}

/** The days a rate change may not alter (issued runs before the current
 *  pay period) and the floor a used rate may change from. Keyed under
 *  `hrKeys.rates(id)` so every rate mutation refreshes it too. */
export interface RateLock {
  floor: string
  locked: { start: string; end: string; year: number; month: number; run: string }[]
  runs: LockedRun[]
}

export function useRateLock(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...hrKeys.rates(id ?? ''), 'lock'],
    queryFn: async () =>
      (await api.get<RateLock>(`/api/hr/employees/${id}/rates/lock`)).data,
    enabled: Boolean(id) && enabled,
  })
}

/** The days logged before the person's first rate (or `before`,
 *  a typed date that would become the first), which no rate pays. */
interface UnratedLoggedDays {
  before: string | null
  count: number
  dates: string[]
}

export function useUnratedLoggedDays(id: string | undefined, before: string | undefined, enabled: boolean) {
  return useQuery({
    // Under `rates(id)`: a rate save's invalidation refreshes it too.
    queryKey: [...hrKeys.rates(id ?? ''), 'unrated-logged-days', before ?? ''],
    queryFn: async () =>
      (
        await api.get<UnratedLoggedDays>(`/api/hr/employees/${id}/rates/unrated-logged-days`, {
          params: before ? { before } : {},
        })
      ).data,
    enabled: Boolean(id) && enabled,
  })
}

/** The Salary-tab save's answer. `frozen_months` is non-empty only when
 *  the save moved the current row's date across Closed / Issued payroll months. */
type SavedRate = EmployeeRate & { frozen_months?: string[] }

export function useSaveRate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: unknown }) =>
      (await api.post<SavedRate>(`/api/hr/employees/${id}/rates`, payload)).data,
    onSuccess: (rate) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.rates(rate.employee_id) })
    },
  })
}

/** The result of moving or deleting one rate row. `frozen_months` are the
 *  Closed / Issued payroll months the change touches, which are not re-priced. */
interface RateChange {
  rate: EmployeeRate | null
  frozen_months: string[]
}

export function useMoveRate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, rateId, date }: { id: string; rateId: string; date: string }) =>
      (
        await api.patch<RateChange>(`/api/hr/employees/${id}/rates/${rateId}`, {
          effective_since: date,
        })
      ).data,
    onSuccess: (_change, { id }) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.rates(id) })
    },
  })
}

export function useDeleteRate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, rateId }: { id: string; rateId: string }) =>
      (await api.delete<RateChange>(`/api/hr/employees/${id}/rates/${rateId}`)).data,
    // The response carries no rate, so invalidate from the id that was sent.
    onSuccess: (_change, { id }) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.rates(id) })
    },
  })
}

/** Per-product role transitions for one person, newest change last -- server-
 *  diffed from their `org_chart` history (`hr.py`'s `role_history` route), so
 *  this never needs `hr.view`, so there is no `enabled` gate
 *  here beyond having an id, because every signed-in user may read it. */
export function useRoleHistory(id: string | undefined) {
  return useQuery({
    queryKey: hrKeys.roleHistory(id ?? ''),
    queryFn: async () =>
      (await api.get<RoleTransition[]>(`/api/hr/employees/${id}/role-history`)).data,
    enabled: Boolean(id),
  })
}

/**
 * The Employee List screen's row colouring, transcribed from `EmpItem.Fill`:
 *
 *   blank            -> none
 *   already past     -> red
 *   within 1 month   -> pink
 *   within 3 months  -> yellow
 *
 * Returned as a token rather than a colour so the page can pick shades that
 * work in both themes -- the original was a light-mode-only app.
 */
export type ContractFlag = 'expired' | 'soon' | 'watch' | 'none'

export function contractFlag(endOfContract: string | null, today = new Date()): ContractFlag {
  if (!endOfContract) return 'none'
  const ends = new Date(`${endOfContract}T00:00:00`)
  if (Number.isNaN(ends.getTime())) return 'none'
  const { year, month, day } = businessYmd(today)
  const midnight = new Date(year, month - 1, day)
  if (ends < midnight) return 'expired'
  const inAMonth = new Date(midnight)
  inAMonth.setMonth(inAMonth.getMonth() + 1)
  if (ends < inAMonth) return 'soon'
  const inThreeMonths = new Date(midnight)
  inThreeMonths.setMonth(inThreeMonths.getMonth() + 3)
  if (ends < inThreeMonths) return 'watch'
  return 'none'
}
